import React from 'react';
import { featureFlagsApiRef, useApi } from '@backstage/core-plugin-api';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';
import { capitalize, LinearProgress, Box, Typography } from '@material-ui/core';
import {

  OverflowTooltip,

  Content,
  Link,
  ContentHeader,
  SupportButton,
} from '@backstage/core-components';
import {
  ChipComponent as Chip,
  ChipType,
  Header,
  Table,
} from '@internal/plugin-components-react';
import { useParams } from 'react-router';
import { clusterManagementApiRef, } from '../api/types';
import SettingsIcon from '@mui/icons-material/Settings';
import { MetricDetailPanel } from './MetricDetailPanel/MetricDetailPanel';
import { DEPLOYMENT_STATUS_FF } from '../constants';
import DeploymentInfo from './DeploymentInfo';

const getColumns = (
  deploymentStatusFF,
) => [
  {
    title: 'Cluster',
    field: 'environment_id',
    cellStyle: { maxWidth: 250 },
    highlight: true,
    render: ({ environment_id }) => {
      return React.createElement(OverflowTooltip, { text: capitalize(environment_id),} );
    },
  },
  {
    title: 'Env',
    field: 'environment_type',
    width: '5%',
    render: ({ environment_type }) => {
      return React.createElement(OverflowTooltip, { text: capitalize(environment_type || '-'),} );
    },
  },
  {
    title: 'AWS ID',
    field: 'aws_account_id',
    width: '15%',
    render: ({ aws_account_id }) => React.createElement(OverflowTooltip, { text: aws_account_id,} ),
  },
  {
    title: 'Version',
    field: 'version',
    width: '5%',
    render: ({ version }) => React.createElement(OverflowTooltip, { text: version,} ),
  },
  {
    title: 'Hash',
    field: 'hash',
    cellStyle: { maxWidth: 180 },
    render: ({ hash }) =>
      hash?.trim() ? (
        React.createElement(Box, { fontFamily: "monospace",}, hash.slice(0, 7))
      ) : (
        React.createElement(Typography, { variant: "subtitle2",}, "No Data" )
      ),
  },
  {
    title: 'Deployment Status',
    field: 'status',
    sorting: true,
    defaultSort: 'desc',
    width: '10%',
    render: ({ status }) => {
      const type = (() => {
        if (status.toLowerCase() === 'healthy') {
          return ChipType.success;
        }

        if (status.toLowerCase() === 'unhealthy') {
          return ChipType.error;
        }

        if (status.toLowerCase() === 'pending') {
          return ChipType.warning;
        }
        return undefined;
      })();

      return (
        React.createElement(Chip, {
          label: capitalize(status.toLowerCase()),
          size: "small",
          isTinyBorder: true,
          type: type,}
        )
      );
    },
  },
  {
    title: 'DEPLOYMENT',
    field: 'deployment_status',
    sorting: false,
    defaultSort: 'desc',
    hidden: !deploymentStatusFF,
    width: '10%',
    render: ({ deployment_status, deployment_version }) => (
      React.createElement(DeploymentInfo, {
        deploymentStatus: deployment_status,
        deplotmentVersion: deployment_version,}
      )
    ),
  },
  {
    width: '10%',
    sorting: false,
    render: ({
      environment_id: environmentId,
      application_id: applicationId,
    }) => {
      return (
        React.createElement(Box, {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: 30,}
        
          , React.createElement(Link, {
            to: `/cluster-management/applications/${applicationId}/${environmentId}`,
            style: { marginRight: 5, width: 24, height: 24 },}
          
            , React.createElement(SettingsIcon, null )
          )
        )
      );
    },
  },
];

const filters = [
  {
    column: 'Cluster',
    type: 'select',
  },
  {
    column: 'Env',
    type: 'select',
  },
  {
    column: 'Deployment Status',
    type: 'select',
  },
  {
    column: 'Version',
    type: 'select',
  },
];

export const AppManagementClusterPage = () => {
  const { applicationId } = useParams();
  const clusterManagementApi = useApi(clusterManagementApiRef);
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const deploymentStatusFF = featureFlagsApi.isActive(DEPLOYMENT_STATUS_FF);

  const {
    value: environmentApplicationsRes,
    loading: isFetchingenvironmentApplicationsRes,
    retry: refetchEnvironmentApplications,
  } = useAsyncRetry(
    async () =>
      clusterManagementApi.getEnvironmentApplications({ applicationId }),
    [],
  );

  return (
    React.createElement(React.Fragment, null
      , React.createElement(Header, {
        title: applicationId,
        breadcrumbItems: [
          { text: 'App Management', href: '/cluster-management/applications' },
          { text: applicationId },
        ],}
      )
      , isFetchingenvironmentApplicationsRes && (
        React.createElement(LinearProgress, { color: "secondary",} )
      )
      , environmentApplicationsRes?.data &&
        environmentApplicationsRes?.data.length > 0 && (
          React.createElement(Content, null
            , React.createElement(ContentHeader, {
              titleComponent: 
                React.createElement(Typography, { variant: "h5",}, "Environments")
              ,}
            
              , React.createElement(SupportButton, null, "All your Environments data"   )
            )
            , React.createElement(Table, {
              options: {
                pageSize: 20,
              },
              columns: getColumns(deploymentStatusFF),
              filters: filters,
              data: environmentApplicationsRes.data,
              isLoading: isFetchingenvironmentApplicationsRes,
              detailPanel: MetricDetailPanel,
              onRefresh: 
                deploymentStatusFF
                  ? () => refetchEnvironmentApplications()
                  : undefined
              ,
              keepDetailExpandedOnRefresh: true,}
            )
          )
        )
    )
  );
};
